<script lang="ts" setup>
interface SeoMeta {
  title?: string
  description?: string
  image?: string
}

interface Props {
  title?: string
  seo?: SeoMeta
}

const props = defineProps<Props>()

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { appFrontEndDomain } = runtimeConfig.public

const pageTitle = computed(() => (route.meta.title as string | undefined) || props.title)

const defaultSeo = {
  title: 'Forgd: A new kind of Web3 investment bank',
  description: 'Forgd provides blockchain projects with free tools and solutions for token launch and protocol optimization—all in a single platform.',
}
const seoTitle = computed(() => props.seo?.title || defaultSeo.title)
const seoDescription = computed(() => props.seo?.description || defaultSeo.description)
const defaultSeoImage = 'https://cdn.prod.website-files.com/6642268ef69d551ef8d67770/66d72c1ba054de8cd491c239_opengraph-forgd.jpg'
const seoImage = computed(() => props.seo?.image || defaultSeoImage)
const imageMimeTypes: Record<string, 'image/png' | 'image/webp' | 'image/gif' | 'image/avif'> = {
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
}
const seoImageType = computed(() => {
  const path = seoImage.value.toLowerCase().split(/[?#]/)[0] ?? ''
  const ext = path.match(/\.([a-z0-9]+)$/)?.[1] ?? ''
  return imageMimeTypes[ext] ?? 'image/jpeg'
})

useHead({
  title: pageTitle,
  htmlAttrs: { class: 'scroll-smooth' },
})

useSeoMeta({
  description: seoDescription,
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  ogImage: seoImage,
  ogImageType: seoImageType,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogType: 'website',
  ogSiteName: 'Forgd',
  ogUrl: () => `${appFrontEndDomain}${route.path}`,
  twitterCard: 'summary_large_image',
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
  twitterImage: seoImage,
})
</script>

<template>
  <NuxtLayout
    name="dashboard"
    container-layout="fluid"
    :container-class="false"
    supports-mobile
    collapsed-nav
  >
    <template #header>
      <div class="flex items-center gap-2">
        <div class="flex shrink-0 flex-col justify-center">
          <span class="text-xs leading-none font-normal text-secondary">
            About Forgd
          </span>
          <span class="text-sm font-semibold text-primal">
            {{ pageTitle }}
          </span>
        </div>
      </div>
    </template>

    <template v-if="$slots['header-auth']" #header-auth>
      <slot name="header-auth" />
    </template>

    <!-- Full-bleed marketing sections manage their own max-width internally. -->
    <slot />

    <template #app-footer>
      <CoreAppFooterBlock />
    </template>
  </NuxtLayout>
</template>
